import { useState, useEffect, useRef } from "react"
import { Button } from "@/components/chadcn/Button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/chadcn/Dialog"
import cn from "@/util/cn"

type HeightPickerProps = {
  valueCm: number | undefined
  onChange: (heightInCentimeters: number) => void
  disabled?: boolean
}

const FEET_OPTIONS = [3, 4, 5, 6]
const INCH_OPTIONS = Array.from({ length: 12 }, (_, i) => i)

const ftInToCm = (feet: number, inches: number): number =>
  Math.round(feet * 30.48 + inches * 2.54)

const cmToFtIn = (cm: number): { feet: number; inches: number } => {
  const totalInches = cm / 2.54
  const feet = Math.floor(totalInches / 12)
  const inches = Math.round(totalInches % 12)
  return { feet, inches }
}

const formatFtIn = (cm: number | undefined): string => {
  if (cm === undefined) return ""
  const { feet, inches } = cmToFtIn(cm)
  return `${feet} feet ${inches} inches`
}

const HeightPicker = ({ valueCm, onChange, disabled }: HeightPickerProps) => {
  const initial = valueCm !== undefined ? cmToFtIn(valueCm) : { feet: 5, inches: 5 }
  const [open, setOpen] = useState(false)
  const [draftFeet, setDraftFeet] = useState(initial.feet)
  const [draftInches, setDraftInches] = useState(initial.inches)

  const inchesColumnRef = useRef<HTMLDivElement | null>(null)
  const selectedInchRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!open) return
    selectedInchRef.current?.scrollIntoView({ block: "center" })
  }, [open])

  const handleOpen = () => {
    if (disabled) return
    const { feet, inches } = valueCm !== undefined ? cmToFtIn(valueCm) : { feet: 5, inches: 5 }
    setDraftFeet(feet)
    setDraftInches(inches)
    setOpen(true)
  }

  const handleApply = () => {
    onChange(ftInToCm(draftFeet, draftInches))
    setOpen(false)
  }

  return (
    <>
      <input
        type="text"
        readOnly
        value={formatFtIn(valueCm)}
        onClick={handleOpen}
        disabled={disabled}
        aria-label="Height"
        className="block w-full cursor-pointer rounded-lg border-0 bg-white px-3 py-2 text-base ring-1 ring-inset ring-input focus:ring-2 focus:ring-primary disabled:text-primary-contrast/60"
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="sr-only">Choose your height</DialogTitle>
          </DialogHeader>
          <div className="flex justify-center gap-4">
            <ColumnPicker
              label="Feet"
              options={FEET_OPTIONS}
              selected={draftFeet}
              onSelect={setDraftFeet}
            />
            <ColumnPicker
              label="Inches"
              options={INCH_OPTIONS}
              selected={draftInches}
              onSelect={setDraftInches}
              columnRef={inchesColumnRef}
              selectedItemRef={selectedInchRef}
            />
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleApply}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

type ColumnPickerProps = {
  label: string
  options: number[]
  selected: number
  onSelect: (value: number) => void
  columnRef?: React.RefObject<HTMLDivElement | null>
  selectedItemRef?: React.RefObject<HTMLButtonElement | null>
}

const ColumnPicker = ({
  label,
  options,
  selected,
  onSelect,
  columnRef,
  selectedItemRef,
}: ColumnPickerProps) => (
  <div className="flex flex-col items-center gap-1">
    <span className="text-sm text-primary-contrast">{label}</span>
    <div
      ref={columnRef}
      className="h-[180px] w-[60px] overflow-y-auto rounded border border-primary-contrast/20"
    >
      {options.map((option) => {
        const isSelected = option === selected
        return (
          <button
            key={option}
            type="button"
            ref={isSelected ? selectedItemRef : undefined}
            onClick={() => onSelect(option)}
            className={cn(
              "block w-full cursor-pointer p-2 text-center text-sm",
              isSelected
                ? "bg-primary text-white"
                : "text-primary-contrast hover:bg-secondary",
            )}
          >
            {option}
          </button>
        )
      })}
    </div>
  </div>
)

export default HeightPicker
